import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Bell, CheckCheck, FileText, AlertTriangle, Clock, Sparkles } from 'lucide-react';

const TYPE_ICON = {
  task_assigned: Clock,
  sla_breach: AlertTriangle,
  doc_processed: Sparkles,
  escalation: AlertTriangle,
};
const TYPE_COLOR = {
  task_assigned: 'text-blue-600 bg-blue-50',
  sla_breach: 'text-red-600 bg-red-50',
  doc_processed: 'text-purple-600 bg-purple-50',
  escalation: 'text-amber-600 bg-amber-50',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
};

const NotificationBell = () => {
  const { notifications, unreadCount, markNotificationRead, markAllNotificationsRead, fetchNotifications } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchNotifications();
  };

  const recent = (notifications || []).slice(0, 10);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={toggle}
        className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="font-semibold text-sm text-slate-800">Notifications</span>
            {recent.length > 0 && (
              <button
                data-testid="mark-all-read-btn"
                onClick={markAllNotificationsRead}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {recent.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-slate-400">
                <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" />
                You're all caught up
              </div>
            ) : (
              recent.map((n) => {
                const Icon = TYPE_ICON[n.type] || FileText;
                const color = TYPE_COLOR[n.type] || 'text-slate-600 bg-slate-50';
                return (
                  <div
                    key={n.id}
                    data-testid={`notification-item-${n.id}`}
                    className="flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50"
                  >
                    <div className={`p-1.5 rounded-lg ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{n.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[11px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    <button
                      data-testid={`mark-read-${n.id}`}
                      onClick={() => markNotificationRead(n.id)}
                      className="text-[11px] text-blue-600 hover:text-blue-700 shrink-0"
                    >
                      Read
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
